/**
 * ASSISTANT DE CLÔTURE — LE RAIL DES ÉTAPES.
 *
 * L'ancienne progression était une rangée de pastilles numérotées : elle
 * disait OÙ l'on était, jamais OÙ EN ÉTAIT la nuit. Le rail répond aux deux
 * questions qu'on se pose réellement à 6 h du matin, une clôture à moitié
 * faite sous les yeux : « qu'est-ce qui reste ? » et « où ça cloche ? ».
 *
 * L'état de chaque étape (✓ faite, ● en cours, ○ à faire, ! écart) est
 * décidé par `closing-rail-model.ts`, pur et testé ; ce fichier le dessine.
 * Sous chaque point de vente, son chiffre clé : ce que les rapports annoncent
 * et l'écart avec le compté. Une source absente se dit, elle ne vaut pas zéro.
 *
 * En page étroite, le même rail devient une bande défilante au-dessus de
 * l'étape (finance-wizard.css) ; l'étape courante y reste toujours visible.
 *
 * ═══ LE MOUVEMENT (retour de l'exploitant, 07/10/2026) ═══
 *
 * « Ça manque d'animation sur la barre de progression. » Trois gestes, tous
 * portés par les jetons du système (finance-wizard.css), tous éteints quand
 * le mouvement est réduit — et aucun dont la FIN commande quoi que ce soit :
 * un onglet caché gèle les animations, l'écran doit rester juste sans elles.
 *
 *   · la jauge se remplit en douceur, et un reflet la traverse quand elle
 *     PROGRESSE (jamais quand elle recule, jamais au chargement) ;
 *   · une pastille qui devient « faite » s'allume, et son ✓ se trace ;
 *   · l'indicateur de l'étape courante GLISSE d'une étape à l'autre : un seul
 *     élément, mesuré sur l'étape courante, au lieu d'un fond qui saute.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { formatMoney } from './money.js'
import { EcartSeuil } from './ClosingCommon.js'
import { etapesAllumees, progression, type DetailEtape, type EtapeRail, type EtatEtape } from './closing-rail-model.js'

export { etatsDesEtapes, type EtapeAssistant, type EtapeRail } from './closing-rail-model.js'

const LU: Record<EtatEtape, string> = {
  'faite': 'faite',
  'en-cours': 'en cours',
  'a-faire': 'à faire',
  'ecart': 'écart à expliquer',
  'facultative': 'facultative',
}

function Detail(props: { detail: DetailEtape; seuils: { warn: number; alert: number } }) {
  const d = props.detail
  if (d.genre === 'texte') return <span className="fwz-rail__detail">{d.texte}</span>
  return (
    <span className="fwz-rail__detail">
      {d.annonce === null ? 'Rien d’annoncé' : `${formatMoney(d.annonce)} annoncés`}
      {d.partiel ? ' · compté en partie' : null}
      {d.ecart !== null
        ? <> · écart <EcartSeuil cents={d.ecart} seuils={props.seuils} /></>
        : d.constate === null ? ' · pas encore compté' : null}
      {d.note ? <span className="fwz-rail__note"> · {d.note}</span> : null}
    </span>
  )
}

export function ClosingRail(props: {
  etapes: EtapeRail[]
  courante: string
  seuils: { warn: number; alert: number }
  onEtape: (cle: string) => void
}) {
  const { etapes, courante, seuils, onEtape } = props
  const liste = useRef<HTMLOListElement>(null)
  const p = progression(etapes)

  /* ─── la jauge : un reflet quand elle progresse ───
     Le reflet est un élément REMONTÉ (clé incrémentée) : son animation joue
     une fois à sa naissance, et rien n'attend qu'elle finisse. */
  const partAvant = useRef<number | null>(null)
  const [reflet, setReflet] = useState(0)
  useEffect(() => {
    const avant = partAvant.current
    partAvant.current = p.part
    if (avant !== null && p.part > avant) setReflet((n) => n + 1)
  }, [p.part])

  /* ─── les pastilles qui s'allument ───
     Seules celles qui DEVIENNENT faites (closing-rail-model) : au chargement,
     l'entrée de la page suffit — vingt ✓ qui s'allument ensemble ne disent
     plus rien. Une étape qui cesse d'être faite perd la classe : elle se
     rallumera si elle le redevient. */
  const etatsAvant = useRef<Map<string, EtatEtape> | null>(null)
  const [allumees, setAllumees] = useState<ReadonlySet<string>>(() => new Set())
  useEffect(() => {
    const neuves = etapesAllumees(etatsAvant.current, etapes)
    etatsAvant.current = new Map(etapes.map((e) => [e.cle, e.etat]))
    setAllumees((avant) => {
      const garde = [...avant].filter((c) => etapes.some((e) => e.cle === c && e.etat === 'faite'))
      if (neuves.length === 0 && garde.length === avant.size) return avant
      return new Set([...garde, ...neuves])
    })
  }, [etapes])

  /* ─── l'indicateur de l'étape courante, qui glisse ───
     Mesuré sur l'étape courante, DANS la liste (repère : `position:
     relative`) : il défile avec elle, en colonne comme en bande. La première
     pose est immédiate — glisser depuis le coin de la liste serait un
     mensonge ; ensuite, chaque changement glisse. La liste qui change de
     taille (une étape dont le détail passe sur deux lignes, la bande qui
     devient colonne) re-mesure. */
  const [curseur, setCurseur] = useState<{ x: number; y: number; l: number; h: number; anime: boolean } | null>(null)
  useLayoutEffect(() => {
    const ol = liste.current
    if (!ol) return
    const mesurer = (anime: boolean) => {
      const on = ol.querySelector<HTMLElement>('[aria-current="step"]')?.closest('li')
      if (!on) { setCurseur(null); return }
      const m = { x: on.offsetLeft, y: on.offsetTop, l: on.offsetWidth, h: on.offsetHeight }
      setCurseur((c) => (c && c.x === m.x && c.y === m.y && c.l === m.l && c.h === m.h ? c : { ...m, anime: anime && c !== null }))
    }
    mesurer(true)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => mesurer(true))
    ro.observe(ol)
    for (const li of ol.querySelectorAll('.fwz-rail__item')) ro.observe(li)
    return () => ro.disconnect()
  }, [courante, etapes])

  /* L'étape courante reste visible DANS le rail : en bande défilante (page
     étroite) comme en colonne plus longue que l'écran. On fait défiler la
     liste elle-même — `scrollIntoView` ferait aussi sauter la page. */
  useEffect(() => {
    const ol = liste.current
    const on = ol?.querySelector<HTMLElement>('[aria-current="step"]')?.closest('li')
    if (!ol || !on) return
    if (ol.scrollWidth > ol.clientWidth + 1) {
      ol.scrollTo({ left: Math.max(0, on.offsetLeft - 16), behavior: 'smooth' })
    } else if (ol.scrollHeight > ol.clientHeight + 1) {
      const haut = on.offsetTop
      const bas = haut + on.offsetHeight
      if (haut < ol.scrollTop || bas > ol.scrollTop + ol.clientHeight) {
        ol.scrollTo({ top: Math.max(0, haut - 16), behavior: 'smooth' })
      }
    }
  }, [courante])

  return (
    <nav className={`fwz-rail${curseur ? ' fwz-rail--curseur' : ''}`} aria-label="Étapes de la clôture">
      <div className="fwz-rail__tete">
        <p className="fwz-rail__titre">Étapes</p>
        <p className="fwz-rail__bilan">
          {p.faites} sur {p.total} faites
          {p.aExpliquer > 0 ? <span className="fwz-rail__alerte"> · {p.aExpliquer} à expliquer</span> : null}
        </p>
        <span className="fwz-rail__jauge" aria-hidden="true">
          <span className="fwz-rail__jauge-plein" style={{ width: `${p.part}%` }}>
            {reflet > 0 ? <span key={reflet} className="fwz-rail__reflet" /> : null}
          </span>
        </span>
      </div>

      <ol className="fwz-rail__liste" ref={liste}>
        {/* L'indicateur est un élément de la liste — il défile avec elle —,
            hors du flux et muet pour les lecteurs d'écran : `aria-current`
            dit déjà quelle étape est la bonne. */}
        {curseur ? (
          <li
            className={`fwz-rail__curseur${curseur.anime ? ' fwz-rail__curseur--anime' : ''}`}
            aria-hidden="true"
            role="presentation"
            style={{
              transform: `translate(${curseur.x}px, ${curseur.y}px)`,
              width: `${curseur.l}px`,
              height: `${curseur.h}px`,
            }}
          />
        ) : null}
        {etapes.map((e) => {
          const on = e.cle === courante
          return (
            <li
              key={e.cle}
              className={`fwz-rail__item fwz-rail__item--${e.etat}${on ? ' fwz-rail__item--on' : ''}${e.posId !== undefined ? ' fwz-rail__item--pos' : ''}${allumees.has(e.cle) ? ' fwz-rail__item--allume' : ''}`}
            >
              <button
                type="button"
                className="fwz-rail__btn"
                aria-current={on ? 'step' : undefined}
                onClick={() => onEtape(e.cle)}
              >
                <span className="fwz-rail__marque" aria-hidden="true">
                  {e.etat === 'faite' ? <Check size={13} strokeWidth={3} /> : e.etat === 'ecart' ? '!' : null}
                </span>
                <span className="fwz-rail__texte">
                  <span className="fwz-rail__label">{e.label}</span>
                  {e.detail ? <Detail detail={e.detail} seuils={seuils} /> : null}
                  <span className="fwz-lu">{` — ${LU[e.etat]}`}</span>
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
